import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Paper, TextField } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import { GoogleMap, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import {
  RMSTextField,
  RMSSelect,
  RMSDateTimePicker,
} from '../../../shared/components/form-controls';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import SectionLabel from './SectionLabel';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import {
  getAddressCoordinateId,
  getAddressCoordinateDetails,
  saveAddressCoordinates,
  linkAddressCoordinate,
} from '../../services/userService';
import {
  DATUM_OPTIONS,
  DICT_SELECT_PROPS,
  DEFAULT_VALUES,
  DEFAULT_CENTER,
} from '../../constants/addressCoordinatesFormConstants';
import { parseCoords, getCoords } from '../../utility/addressCoordinatesHelper';

const AddressCoordinatesTab = forwardRef(
  ({ ptsAddressID, fullAddressText, onStateChange }, ref) => {
    const dispatch = useDispatch();
    const canEditAddress = getFullPermissions('globals', 'Addresses', 'any').Edit;
    const isViewOnly = !canEditAddress;
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [coordinate, setCoordinate] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [searchBox, setSearchBox] = useState(null);
    const [searchText, setSearchText] = useState('');

    const { measureOptions } = useUserDictionaryOptions();

    const methods = useForm({
      mode: 'onSubmit',
      defaultValues: DEFAULT_VALUES,
      disabled: isViewOnly,
    });

    const { control, setValue } = methods;
    const [Latitude, Longitude] = useWatch({ control, name: ['Latitude', 'Longitude'] });
    const { isDirty } = useFormState({ control });
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const markerPosition = useMemo(
      () =>
        Latitude !== '' && Longitude !== '' && Latitude != null && Longitude != null
          ? { lat: Number(Latitude), lng: Number(Longitude) }
          : null,
      [Latitude, Longitude]
    );

    const loadCoordinate = useCallback(async () => {
      if (!ptsAddressID) return;

      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const ptsCoordinateID = await getAddressCoordinateId(ptsAddressID);
          if (!ptsCoordinateID) return null;
          return getAddressCoordinateDetails(ptsCoordinateID);
        },
        onSuccess: (data) => {
          setCoordinate(data || null);
          methods.reset({
            ...DEFAULT_VALUES,
            ...getCoords(data),
            UncertaintyDistance: data?.UnCertaintyDistance ?? '',
            Datum: data?.Datum || '',
            CoordinateDT: data?.CoordinateDT || null,
            Altitude: data?.Altitude ?? '',
            AltitudeRefPoint: data?.AltitudeRefPoint ?? '',
            CellID: data?.CellIDText || '',
            SectorID: data?.CellSectorIDText || '',
            Speed: data?.Speed ?? '',
            AltitudeUnitMeasure: data?.AltitudeUnitMeasure || '',
            SpeedUnitMeasure: data?.SpeedUnitMeasure || '',
          });
        },
      });
    }, [ptsAddressID, dispatch, methods]);

    const handleMapClick = useCallback(
      (event) => {
        if (isViewOnly) return;
        setValue('Latitude', event.latLng.lat(), { shouldDirty: true });
        setValue('Longitude', event.latLng.lng(), { shouldDirty: true });
      },
      [isViewOnly, setValue]
    );

    const onPlacesChanged = useCallback(() => {
      if (isViewOnly) return;
      const places = searchBox?.getPlaces();
      const place = places?.[0];
      if (!place?.geometry?.location) return;

      setValue('Latitude', place.geometry.location.lat(), { shouldDirty: true });
      setValue('Longitude', place.geometry.location.lng(), { shouldDirty: true });
    }, [isViewOnly, searchBox, setValue]);

    const handleUseCurrentAddress = useCallback(() => setSearchText(fullAddressText || ''), [
      fullAddressText,
    ]);

    const handleShowCurrentLocation = useCallback(() => {
      if (mapInstance && markerPosition) mapInstance.panTo(markerPosition);
    }, [mapInstance, markerPosition]);

    const handleSearchTextChange = useCallback((event) => setSearchText(event.target.value), []);

    const onSubmit = useCallback(
      async (data) => {
        const payload = {
          ...parseCoords(data.Latitude, data.Longitude),
          UnCertaintyDistance: data.UncertaintyDistance || null,
          Datum: data.Datum || null,
          CoordinateDT: data.CoordinateDT || null,
          Altitude: data.Altitude || null,
          AltitudeRefPoint: data.AltitudeRefPoint || null,
          CellIDText: data.CellID || null,
          CellSectorIDText: data.SectorID || null,
          Speed: data.Speed || null,
          AltitudeUnitMeasure: data.AltitudeUnitMeasure || null,
          SpeedUnitMeasure: data.SpeedUnitMeasure || null,
          ptsCoordinateID: coordinate?.ptsCoordinateID || null,
        };

        await executeApiAction({
          setLoading: setSubmitting,
          dispatch,
          action: async () => {
            const saved = await saveAddressCoordinates(payload);
            const ptsCoordinateID = saved?.ptsCoordinateID || payload.ptsCoordinateID;
            if (ptsCoordinateID) {
              await linkAddressCoordinate(ptsAddressID, ptsCoordinateID);
            }
          },
          onSuccess: () => {
            dispatch(notify('Coordinate saved successfully', 'success'));
            loadCoordinate();
          },
        });
      },
      [coordinate, ptsAddressID, dispatch, loadCoordinate]
    );

    const handleSave = methods.handleSubmit(onSubmit);

    useImperativeHandle(
      ref,
      () => ({
        isDirty: () => isDirtyRef.current,
        save: () => handleSave(),
      }),
      [handleSave]
    );

    useEffect(() => {
      loadCoordinate();
    }, [loadCoordinate]);

    useEffect(() => {
      onStateChange?.({ coordinate, submitting: submitting || loading });
    }, [coordinate, submitting, loading, onStateChange]);

    return (
      <Box className="rms-dialog-section" mt={2}>
        <FormProvider {...methods}>
          <SectionLabel>{fullAddressText || 'Coordinates'}</SectionLabel>

          <Box mb={2} className="address-coordinates-tab__map">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={markerPosition || DEFAULT_CENTER}
              zoom={markerPosition ? 15 : 10}
              onLoad={setMapInstance}
              onClick={handleMapClick}>
              <StandaloneSearchBox onLoad={setSearchBox} onPlacesChanged={onPlacesChanged}>
                <Paper elevation={3} className="address-coordinates-tab__search-box">
                  <RMSIconButton
                    icon={<ArrowForwardIcon fontSize="small" />}
                    tooltip="Use current address"
                    disabled={!fullAddressText}
                    onClick={handleUseCurrentAddress}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Address Search"
                    value={searchText}
                    onChange={handleSearchTextChange}
                  />
                  <RMSIconButton
                    icon={<MyLocationIcon fontSize="small" />}
                    tooltip="Show current location"
                    disabled={!markerPosition}
                    onClick={handleShowCurrentLocation}
                  />
                </Paper>
              </StandaloneSearchBox>
              {markerPosition && <Marker position={markerPosition} />}
            </GoogleMap>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={3}>
              <RMSTextField
                name="Latitude"
                label="Latitude"
                type="number"
                inputProps={{ min: -90, max: 90 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="Longitude"
                label="Longitude"
                type="number"
                inputProps={{ min: -180, max: 180 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="UncertaintyDistance"
                label="Uncertainty Distance"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="Datum"
                label="Datum"
                options={DATUM_OPTIONS}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSDateTimePicker name="CoordinateDT" label="Coordinate Date Time" />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="Altitude"
                label="Altitude"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="AltitudeRefPoint"
                label="Point Of Reference"
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="AltitudeUnitMeasure"
                label="Altitude Units"
                options={measureOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="CellID" label="CellID" inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="SectorID" label="SectorID" inputProps={{ maxLength: 2000 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField name="Speed" label="Speed" type="number" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="SpeedUnitMeasure"
                label="Speed Units"
                options={measureOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Box>
    );
  }
);

export default AddressCoordinatesTab;
